import { useState, useEffect } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { userDataService } from '../services/UserDataService';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Hook for managing continue watching data from UserDataService
 * @returns {Object} Continue watching state and methods
 */
export const useContinueWatching = () => {
  const [continueWatchingData, setContinueWatchingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentProfile } = useProfile();
  const { isAuthenticated, user } = useAuthContext();

  useEffect(() => {
    const fetchContinueWatchingData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isAuthenticated || !currentProfile) {
          setContinueWatchingData([]);
          return;
        }

        // Fetch fresh resumption data from API for current profile and update internal cache
        await userDataService.refreshResumptionsData();

        // Get enhanced continue watching data that includes series with completed episodes
        const continueWatchingItems = await userDataService.getEnhancedContinueWatchingData();

        if (!continueWatchingItems || continueWatchingItems.length === 0) {
          setContinueWatchingData([]);
          setLoading(false);
          return;
        }

        // Filter to show only the latest episode per series
        const latestEpisodesPerSeries =
          userDataService.getLatestEpisodesPerSeries(continueWatchingItems);

        // Read + parse the cached AppLoad payload ONCE, not once per item, and
        // index the channels by id so the dspro_id lookup below is O(1).
        const dsproIdBySpotlightId = new Map();
        try {
          const cachedAppLoadRaw = localStorage.getItem('bkfc_cache_APP_LOAD_DATA');
          if (cachedAppLoadRaw) {
            const cachedAppLoad = JSON.parse(cachedAppLoadRaw).data;
            const homepageData =
              cachedAppLoad?.data?.homepage || cachedAppLoad?.homepage || cachedAppLoad;

            if (Array.isArray(homepageData)) {
              homepageData.forEach(category => {
                (category.channels || []).forEach(cachedChannel => {
                  if (cachedChannel._id && cachedChannel.dspro_id) {
                    dsproIdBySpotlightId.set(cachedChannel._id, cachedChannel.dspro_id);
                  }
                });
              });
            }
          }
        } catch (e) {
          console.warn('useContinueWatching: Failed to lookup dspro_id from cache:', e);
        }

        // Transform the resumption data into a format compatible with CategoryRail
        const transformedData = latestEpisodesPerSeries.map(item => {
          // Create a channel-like object from resumption data
          const channel = {
            // Use the real series ID for navigation, not the mock resumption ID
            _id: item.parent_channel?._id || item.channel?._id || item._id,
            title: item.title,
            // Use parent channel description for series, channel description for movies
            description:
              item.parent_channel?.description ||
              item.channel?.description ||
              item.description ||
              '',
            // Use the correct slug from parent channel or channel, not the resumption ID
            slug: item.parent_channel?.slug || item.channel?.slug || item.slug,
            // Use parent_channel images for series, channel images for individual content
            spotlight_poster:
              item.parent_channel?.spotlight_poster || item.channel?.spotlight_poster,
            poster: item.parent_channel?.poster || item.channel?.poster,
            thumb: item.thumb,
            video_id: item.video_id,
            channel_type: item.channel?.programming_type || 'video',
            programming_type:
              item.parent_channel?.programming_type || item.channel?.programming_type || 'Movie',
            rating: item.rating,
            // Add resumption-specific data with proper episode metadata
            resumption_data: {
              point: item.point,
              progress: item.progress,
              duration: item.duration, // Include duration for proper progress calculation
              updatedAt: item.updatedAt,
              parent_channel: item.parent_channel,
              channel: item.channel,
              // Extract episode and season numbers properly from the enhanced data
              episodeNumber: item.episode || item.channel?.episode_number || 1,
              seasonNumber: item.season || item.channel?.season_number || 1,
            },
          };

          // If this is a series episode, use parent channel data
          if (item.parent_channel) {
            channel.parent_channel_id = item.parent_channel._id;
            channel.parent_channel_title = item.seriestitle || item.parent_channel.title; // Use seriestitle from API
            channel.episode_title = item.title;
            channel.channel_type = 'series';
            channel.programming_type = 'Series';
          }

          // Look up the actual DS Pro ID from cached AppLoad data
          const spotlightId = item.parent_channel?._id || item.channel?._id;
          const actualDsproId = spotlightId ? dsproIdBySpotlightId.get(spotlightId) : null;

          // Set the DS Pro ID for reactions API
          channel.dspro_id = actualDsproId || spotlightId; // Use actual DS Pro ID or fallback to Spotlight ID
          channel.contentId = actualDsproId || spotlightId; // Same for watchlist operations

          return channel;
        });

        setContinueWatchingData(transformedData);
      } catch (err) {
        console.error('Error fetching continue watching data:', err);
        setError("We couldn't load Continue Watching right now. Please try again.");
        setContinueWatchingData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContinueWatchingData();
  }, [isAuthenticated, currentProfile]);

  return {
    continueWatchingData,
    loading,
    error,
    hasContinueWatching: continueWatchingData.length > 0,
  };
};
